import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Shield, FileText, CheckCircle2, Upload, ChevronRight, ChevronLeft,
  Building2, AlertTriangle, Lock, Home
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';

const STEPS = [
  { id: 'identity', label: 'Identity Verification', icon: User },
  { id: 'agreements', label: 'Platform Agreements', icon: FileText },
  { id: 'complete', label: 'Ready to List', icon: CheckCircle2 }
];

export const SellerOnboardingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});

  // KYC Form State
  const [kycForm, setKycForm] = useState({
    document_type: 'emirates_id',
    document_number: '',
    document_front_url: '',
    document_back_url: '',
    expiry_date: ''
  });

  // Agreements State
  const [agreements, setAgreements] = useState({
    settlement_protocol_accepted: false,
    exclusivity_clause_accepted: false,
    data_processing_accepted: false
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/seller/profile');
      setProfile(res.data);
      
      // Determine current step based on status
      if (res.data.kyc_status === 'pending') {
        setCurrentStep(0);
      } else if (!res.data.agreements_accepted) {
        setCurrentStep(1);
      } else {
        setCurrentStep(2);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKycSubmit = async () => {
    // Validation
    const newErrors = {};
    if (!kycForm.document_number) newErrors.document_number = 'Document number is required';
    if (!kycForm.document_front_url) newErrors.document_front_url = 'Front image is required';
    if (!kycForm.expiry_date) newErrors.expiry_date = 'Expiry date is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/seller/kyc', kycForm);
      setCurrentStep(1);
      fetchProfile();
    } catch (error) {
      console.error('KYC submission failed:', error);
      setErrors({ submit: error.response?.data?.detail || 'Failed to submit KYC' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAgreementsSubmit = async () => {
    if (!agreements.settlement_protocol_accepted || !agreements.exclusivity_clause_accepted || !agreements.data_processing_accepted) {
      setErrors({ agreements: 'All agreements must be accepted' });
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/seller/agreements', agreements);
      setCurrentStep(2);
      fetchProfile();
    } catch (error) {
      console.error('Agreements submission failed:', error);
      setErrors({ submit: error.response?.data?.detail || 'Failed to submit agreements' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (field, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/upload/image?category=document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.url) {
        setKycForm(prev => ({ 
          ...prev, 
          [field]: `${process.env.REACT_APP_BACKEND_URL}${response.data.url}` 
        }));
      }
    } catch (error) {
      console.error('Failed to upload document:', error);
      setErrors(prev => ({ ...prev, [field]: 'Failed to upload. Please try again.' }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-background" data-testid="seller-onboarding">
      {/* Header with Navigation */}
      <header className="border-b border-white/10 bg-secondary/20">
        <div className="max-w-3xl mx-auto px-6 py-6">
          {/* Navigation Row */}
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/seller/dashboard')}
              className="text-muted-foreground hover:text-foreground"
              data-testid="dashboard-btn"
            >
              <Home className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
          </div>
          <h1 className="font-display text-2xl mb-2">Seller Verification</h1>
          <p className="text-sm text-muted-foreground">
            Complete verification to start listing properties on Rashii
          </p>
          <Progress value={progress} className="h-1 mt-4" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Step Indicators */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((step, idx) => (
            <div key={step.id} className="flex items-center">
              <div className={`
                flex items-center gap-2 px-4 py-2 rounded-full
                ${idx <= currentStep ? 'bg-primary/20 text-primary' : 'bg-secondary/30 text-muted-foreground'}
              `}>
                <step.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{step.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <ChevronRight className="w-5 h-5 mx-2 text-muted-foreground" />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        {currentStep === 0 && (
          <Card className="glass-card border-white/10" data-testid="kyc-step">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Identity Verification
              </CardTitle>
              <CardDescription>
                Upload your Emirates ID or Passport for identity verification
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {profile?.kyc_status === 'under_review' ? (
                <div className="p-6 rounded bg-gold-600/10 border border-gold-600/30 text-center">
                  <AlertTriangle className="w-8 h-8 text-gold-400 mx-auto mb-3" />
                  <p className="font-medium mb-1">Documents Under Review</p>
                  <p className="text-sm text-muted-foreground">
                    Your identity documents are being verified. This usually takes 24-48 hours.
                  </p>
                  <Button className="mt-4" onClick={() => setCurrentStep(1)}>
                    Continue to Agreements
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Document Type</Label>
                    <Select 
                      value={kycForm.document_type} 
                      onValueChange={(val) => setKycForm(prev => ({ ...prev, document_type: val }))}
                    >
                      <SelectTrigger data-testid="document-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="emirates_id">Emirates ID</SelectItem>
                        <SelectItem value="passport">Passport</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Document Number *</Label>
                    <Input
                      placeholder={kycForm.document_type === 'emirates_id' ? '784-XXXX-XXXXXXX-X' : 'Passport Number'}
                      value={kycForm.document_number}
                      onChange={(e) => setKycForm(prev => ({ ...prev, document_number: e.target.value }))}
                      data-testid="document-number-input"
                    />
                    {errors.document_number && <p className="text-xs text-red-400">{errors.document_number}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Front Image *</Label>
                      <div 
                        className={`
                          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                          ${kycForm.document_front_url ? 'border-green-600/50 bg-green-600/5' : 'border-white/20 hover:border-primary/50'}
                        `}
                        onClick={() => document.getElementById('front-upload').click()}
                      >
                        {kycForm.document_front_url ? (
                          <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto" />
                        ) : (
                          <Upload className="w-8 h-8 text-muted-foreground mx-auto" />
                        )}
                        <p className="text-sm text-muted-foreground mt-2">
                          {kycForm.document_front_url ? 'Uploaded' : 'Click to upload'}
                        </p>
                        <input
                          id="front-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload('document_front_url', e.target.files[0])}
                        />
                      </div>
                      {errors.document_front_url && <p className="text-xs text-red-400">{errors.document_front_url}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label>Back Image {kycForm.document_type === 'emirates_id' ? '*' : '(Optional)'}</Label>
                      <div 
                        className={`
                          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                          ${kycForm.document_back_url ? 'border-green-600/50 bg-green-600/5' : 'border-white/20 hover:border-primary/50'}
                        `}
                        onClick={() => document.getElementById('back-upload').click()}
                      >
                        {kycForm.document_back_url ? (
                          <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto" />
                        ) : (
                          <Upload className="w-8 h-8 text-muted-foreground mx-auto" />
                        )}
                        <p className="text-sm text-muted-foreground mt-2">
                          {kycForm.document_back_url ? 'Uploaded' : 'Click to upload'}
                        </p>
                        <input
                          id="back-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload('document_back_url', e.target.files[0])}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Expiry Date *</Label>
                    <Input
                      type="date"
                      value={kycForm.expiry_date}
                      onChange={(e) => setKycForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                      data-testid="expiry-date-input"
                    />
                    {errors.expiry_date && <p className="text-xs text-red-400">{errors.expiry_date}</p>}
                  </div>

                  {errors.submit && (
                    <div className="p-3 rounded bg-red-600/10 border border-red-600/30 text-red-400 text-sm">
                      {errors.submit}
                    </div>
                  )}

                  <Button 
                    className="w-full" 
                    onClick={handleKycSubmit} 
                    disabled={submitting}
                    data-testid="submit-kyc-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit for Verification'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {currentStep === 1 && (
          <Card className="glass-card border-white/10" data-testid="agreements-step">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Platform Agreements
              </CardTitle>
              <CardDescription>
                Review and accept the Rashii platform terms to proceed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Settlement Protocol */}
              <div className="p-4 rounded border border-white/10 bg-secondary/10">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="settlement"
                    checked={agreements.settlement_protocol_accepted}
                    onCheckedChange={(checked) => setAgreements(prev => ({ ...prev, settlement_protocol_accepted: checked }))}
                    data-testid="settlement-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="settlement" className="text-sm font-medium cursor-pointer">
                      Rashii Settlement Protocol *
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I agree that all property transactions initiated through Rashii will be executed 
                      and settled through the Rashii platform infrastructure, ensuring compliance with 
                      DLD regulations and secure escrow handling.
                    </p>
                  </div>
                </div>
              </div>

              {/* Exclusivity Clause */}
              <div className="p-4 rounded border border-white/10 bg-secondary/10">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="exclusivity"
                    checked={agreements.exclusivity_clause_accepted}
                    onCheckedChange={(checked) => setAgreements(prev => ({ ...prev, exclusivity_clause_accepted: checked }))}
                    data-testid="exclusivity-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="exclusivity" className="text-sm font-medium cursor-pointer">
                      Exclusivity Clause *
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I understand that while I may list my property on other platforms, any buyer 
                      expressing serious interest through Rashii must complete the transaction through 
                      Rashii's verified settlement process. This protects both parties and ensures 
                      transaction integrity.
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Processing */}
              <div className="p-4 rounded border border-white/10 bg-secondary/10">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="data"
                    checked={agreements.data_processing_accepted}
                    onCheckedChange={(checked) => setAgreements(prev => ({ ...prev, data_processing_accepted: checked }))}
                    data-testid="data-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="data" className="text-sm font-medium cursor-pointer">
                      Data Processing & Privacy *
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I consent to Rashii processing my property data, documents, and transaction 
                      information for verification, analytics, and platform services. My data will 
                      be handled in accordance with UAE data protection laws.
                    </p>
                  </div>
                </div>
              </div>

              {errors.agreements && (
                <div className="p-3 rounded bg-red-600/10 border border-red-600/30 text-red-400 text-sm">
                  {errors.agreements}
                </div>
              )}

              <Button 
                className="w-full" 
                onClick={handleAgreementsSubmit} 
                disabled={submitting}
                data-testid="submit-agreements-btn"
              >
                {submitting ? 'Submitting...' : 'Accept & Continue'}
              </Button>
            </CardContent>
          </Card>
        )}

        {currentStep === 2 && (
          <Card className="glass-card border-green-600/30" data-testid="complete-step">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-display mb-2">
                {profile?.seller_status === 'active' 
                  ? 'Verification Complete!' 
                  : 'Submission Complete!'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {profile?.seller_status === 'active' 
                  ? 'Your seller account is now active. You can start listing properties.'
                  : 'Your documents have been submitted and are under review. You can proceed to create property listings while we verify your details.'}
              </p>
              
              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto mb-6">
                <div className="p-4 rounded bg-secondary/20 text-center">
                  <Shield className="w-6 h-6 text-primary mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">KYC Status</p>
                  <Badge variant={profile?.kyc_status === 'verified' || profile?.kyc_status === 'approved' ? 'default' : 'gold'}
                    className={profile?.kyc_status === 'verified' || profile?.kyc_status === 'approved' ? 'bg-green-600/20 text-green-400' : ''}>
                    {profile?.kyc_status === 'under_review' ? 'Under Review' : profile?.kyc_status}
                  </Badge>
                </div>
                <div className="p-4 rounded bg-secondary/20 text-center">
                  <FileText className="w-6 h-6 text-primary mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">Agreements</p>
                  <Badge className="bg-green-600/20 text-green-400">Accepted</Badge>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/seller/dashboard')}>
                  Go to Dashboard
                </Button>
                <Button onClick={() => navigate('/seller/property/create')} className="btn-gold-metallic">
                  <Building2 className="w-4 h-4 mr-2" /> Create Listing
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SellerOnboardingPage;
